<template>
  <v-card :disabled="isProcessing" :loading="isProcessing">
    <template v-slot:progress>
      <v-progress-linear
        absolute
        color="success"
        height="4"
        indeterminate
      ></v-progress-linear>
    </template>
    <v-tabs v-model="tab" class="crm-color" left dark icons-and-text>
      <v-tabs-slider></v-tabs-slider>
      <v-tab href="#tab-1">
        <v-toolbar-title>{{ $t("PIPELINES.TITLE") }}</v-toolbar-title>
      </v-tab>
      <v-spacer></v-spacer>
      <div class="buttons-tab">
        <v-btn
          v-if="hasPermissions('pipelines-create')"
          class="mr-4 success"
          :disabled="isProcessing"
          @click="handleCreateClick"
        >
          {{ $t("GENERAL.LABELS.CREATE_BUTTON") }}
        </v-btn>
      </div>
    </v-tabs>
    <v-tabs-items v-model="tab">
      <v-tab-item value="tab-1">
        <v-card flat>
          <v-card-text class="pa-0">
            <Table
              v-if="userStatePrepared"
              ref="pipelines-table"
              height="500"
              name="pipelines-table"
              :headers="headers"
              :blink-row-item-key="blinkRowItemKey"
              :server-data-options="{
                method: tableItemsMethod,
              }"
              :items-per-page="100"
              :loading="isProcessing"
              dense
              fixed-header
              multi-sort
              filterable
            >
              <template
                v-for="(header, index) in headers"
                v-slot:[`item.${header.value}`]="{ item }"
              >
                <span
                  :key="index"
                  v-if="header.value === 'options'"
                  class="d-flex align-center justify-center flex-nowrap"
                >
                  <v-btn
                    icon
                    color="indigo"
                    @click="handleViewClick(item.item.id)"
                  >
                    <v-icon dark>
                      mdi-eye
                    </v-icon>
                  </v-btn>
                  <v-btn
                    v-if="hasPermissions('pipelines-edit')"
                    icon
                    color="primary"
                    @click="handleEditClick(item.item.id)"
                  >
                    <v-icon dark>
                      mdi-pencil
                    </v-icon>
                  </v-btn>
                  <v-btn
                    v-if="hasPermissions('pipelines-delete')"
                    icon
                    color="red"
                    @click="handleDeleteClick(item.item.id)"
                  >
                    <v-icon dark>
                      mdi-trash-can
                    </v-icon>
                  </v-btn>
                </span>
                <template v-else-if="header.value === 'active'">
                  <v-chip
                    x-small
                    :color="item.value ? 'success' : 'error'"
                    dark
                  >
                    {{ item.value ? $t("GENERAL.LABELS.ACTIVE") : $t("GENERAL.LABELS.INACTIVE") }}
                  </v-chip>
                </template>
                <template v-else-if="header.value === 'stages'">
                  {{ formatStages(item.value) }}
                </template>
                <template v-else>
                  {{ item.value }}
                </template>
              </template>
            </Table>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
    <Confirm
      v-model="deleteConfirm"
      :content="$t('GENERAL.DELETE_CONFIRM')"
      @confirm="deletePipeline"
      @decline="deleteConfirm = false"
    />
  </v-card>
</template>

<script>
import Table from "@/views/partials/v-table/Table.vue";
import Confirm from "@/views/partials/dialogs/Confirm.vue";
import { SHOW_SNACK } from "@/store/snack.module";
import { GET_PIPELINES, DELETE_PIPELINE } from "@/store/pipelines.module";
import userStateService from "@/common/userState.service.js";

export default {
  name: "ListPipelines",
  components: {
    Table,
    Confirm,
  },
  data() {
    return {
      tableName: "pipelines-table",
      blinkRowItemKey: null,
      userStatePrepared: false,
      tab: "tab-1",
      isProcessing: false,
      tableItemsMethod: GET_PIPELINES,
      deleteConfirm: false,
      pipelineForDelete: null,
      headers: [
        {
          text: "#",
          value: "id",
          align: "center",
          width: "10%",
        },
        {
          text: this.$t("GENERAL.LABELS.NAME"),
          value: "name",
          align: "center",
          width: "30%",
        },
        {
          text: this.$t("PIPELINES.STAGES"),
          value: "stages",
          align: "center",
          width: "35%",
          sortable: false,
        },
        {
          text: this.$t("GENERAL.LABELS.STATUS"),
          value: "active",
          align: "center",
          width: "15%",
        },
        {
          text: this.$t("GENERAL.TABLE.OPTIONS_COLUMN"),
          value: "options",
          sortable: false,
          align: "center",
          width: "15%",
        },
      ],
    };
  },
  mounted() {
    this.blinkRowItemKey = userStateService.getState(
      `table-state.${this.tableName}.blinkRowItemKey`,
      null
    );

    this.userStatePrepared = true;
  },
  methods: {
    getErrorMessage(error) {
      if (error && error.errors && typeof error.errors === "object") {
        const firstErrorField = Object.keys(error.errors)[0];
        const firstErrorList = error.errors[firstErrorField];

        if (Array.isArray(firstErrorList) && firstErrorList.length) {
          return firstErrorList[0];
        }
      }

      if (error && error.message) {
        return error.message;
      }

      return this.$t("GENERAL.UNEXPECTED_ERROR");
    },
    formatStages(stages) {
      if (!Array.isArray(stages) || !stages.length) {
        return "-";
      }
      return stages
        .map((s) => s.name)
        .filter(Boolean)
        .join(", ");
    },
    handleCreateClick() {
      this.$router.push({ name: "pipelines-create" });
    },
    handleViewClick(id) {
      userStateService.setState(
        `table-state.${this.tableName}.blinkRowItemKey`,
        id
      );

      this.$router.push({ name: "pipelines-view", params: { id } });
    },
    handleEditClick(id) {
      userStateService.setState(
        `table-state.${this.tableName}.blinkRowItemKey`,
        id
      );

      this.$router.push({ name: "pipelines-edit", params: { id } });
    },
    handleDeleteClick(id) {
      this.pipelineForDelete = id;
      this.deleteConfirm = true;
    },
    deletePipeline() {
      this.isProcessing = true;

      this.$store
        .dispatch(DELETE_PIPELINE, this.pipelineForDelete)
        .then(() => {
          this.$refs["pipelines-table"].fetchServerData();
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          });
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            type: "error",
            message: this.getErrorMessage(error),
          });
        })
        .finally(() => {
          this.isProcessing = false;
          this.deleteConfirm = false;
        });
    },
  },
};
</script>
